import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import IntegrationConnector from '../components/IntegrationConnector'
import { APP_URL, SALESFORCE_CLIENT_ID } from 'constants/ResourcesConstants'
import ACCESS, { Gates } from 'utils/Access'
import { patchOperations } from 'utils/PatchHelper'
import withNotifications from 'components/shared/notifications'
import { refreshCurrentUser } from 'app/modules/user/actions'
import { patchOrgProperties } from 'app/modules/org/actions'
import app from 'containers/app'
import { Icon } from '@driftt/tide-core-legacy'
import RevenueDashboardToggle from '../components/RevenueDashboardToggle'
import { settingsAppConfig } from 'app/modules/user/selectors'

const SALESFORCE_REVENUE_ORG_PROPERTY = 'salesforceRevenueMetricsEnabled'
const ANONYMOUS_VISITOR_OVERVIEW_MESSAGE = `Create new leads and contacts in Salesforce when they interact with Drift`
const ACTIVITY_OVERVIEW_MESSAGE = `Add Drift activity and attach chat transcripts to new and existing lead and contact records in Salesforce`
const CONTACT_OVERVIEW_MESSAGE = `Route Salesforce owners into Drift conversations with their known accounts, contacts, and leads`
const CONTACT_LINK_TEXT = 'conversation routing rules'


class Salesforce extends Component {

  static contextTypes = {
    app: PropTypes.object.isRequired
  }

  onIntegrationConnected = () => {
    app.queries.IntegrationsQueries.fetchIntegrationStates()
    this.enableRevenueDashboard()
  }

  renderSalesforceMappingLink() {
    return (
      <Link to="/settings/salesforce/mapping/attributes" className="integration-config-link">
        <h2 className="integration-section-title">
          Mapped Salesforce attributes
        </h2>
        <p>Map which fields you want to sync between Drift and Salesforce</p>
      </Link>
    )
  }

  renderSalesforceTogglesLink() {
    return (
      <Link to="/settings/salesforce/config" className="integration-config-link">
        <h2 className="integration-section-title">
          Sync settings
        </h2>
        <p>Choose which Salesforce objects are sent between Drift and Salesforce</p>
      </Link>
    )
  }

  renderSalesforceUserMappingLink() {
    return (
      <Link to="/settings/salesforce/mapping/users" className="integration-config-link">
        <h2 className="integration-section-title">
          Mapped Salesforce users
        </h2>
        <p>Map your Drift teammates to your Salesforce teammates</p>
      </Link>
    )
  }

  renderSalesforceChatMetricsLink() {
    return (
      <Link to="/settings/salesforce/mapping/attributes/metrics" className="integration-config-link">
        <h2 className="integration-section-title">
          Send chat metrics to Salesforce
        </h2>
        <p>Send metrics about your conversations to your Salesforce conversation activities</p>
      </Link>
    )
  }

  renderSalesforceSandboxConnector() {
    return (
      <div className="salesforce-sandbox-connector">
        <button className="drift-button">Connect to Sandbox</button>
      </div>
    )
  }

  enableRevenueDashboard = () => {
    const patchedProperties = patchOperations({
      [[SALESFORCE_REVENUE_ORG_PROPERTY]] : true
    })

    this.props.actions.patchOrgProperties(patchedProperties)
  }

  render() {
    const isConnected = this.props.isConnected
    const { settingsAppConfig: { hasSalesforce, hasSalesforceRouting, hasSalesforceRevenueDashboard, hasSalesforceContactSync, hasSalesforceUserMapping, hasSalesforceAttributesMapping } } = this.props

    const hasAccess = hasSalesforce
    const SalesforcePayload = {
      client_id: SALESFORCE_CLIENT_ID,
      response_type: 'code',
      redirect_uri: `${APP_URL}/accept-integration/salesforce`
    }

    const SandboxPayload = {
      client_id: SALESFORCE_CLIENT_ID,
      response_type: 'code',
      redirect_uri: `${APP_URL}/accept-integration/salesforceSandbox`
    }

    const SaleseforceAuthUrl = 'https://login.salesforce.com/services/oauth2/authorize'
    const SandboxURL = 'https://test.salesforce.com/services/oauth2/authorize'

    return (

      <div className="salesforce-integration-connector">

        <div className="salesforce-overview-container">
          {hasSalesforceContactSync && <div className="salesforce-overview-description">
            <Icon className="anonymous-visitor-icon" width={28} name="rolodex"/>
            <p>{ANONYMOUS_VISITOR_OVERVIEW_MESSAGE}</p>
          </div> }
          <div className="salesforce-overview-description">
            <Icon className="activity-icon" width={28} height={22} name="activity"/>
            <p>{ACTIVITY_OVERVIEW_MESSAGE}</p>
          </div>
          {hasSalesforceRouting &&
            <div className="salesforce-overview-description">
              <Icon className="user-target-icon" width={50} name="user-target"/>
              <p>{CONTACT_OVERVIEW_MESSAGE} (👉 you can set this up in your <a href="/settings/rules">{CONTACT_LINK_TEXT}</a>).</p>
            </div>
          }

          {!isConnected && <p className='salesforce-overview-description'>
            Having permission issues?&nbsp;<a href="https://gethelp.drift.com/hc/en-us/articles/360019665113-How-to-Grant-the-Proper-Salesforce-User-and-Object-Permissions-for-Drift-and-Salesforce" target="_blank">Check out this help doc!</a>
          </p>}
          <p className='salesforce-overview-description'>
            Learn more about our Salesforce integration&nbsp;<a href="https://gethelp.drift.com/hc/en-us/articles/360018794974-How-To-Integrate-Drift-With-Salesforce" target="_blank">here!</a>
          </p>
        </div>

        {!isConnected &&
          <div className="connect-container">
            <IntegrationConnector
              onIntegrationConnected={this.onIntegrationConnected}
              integrationId="salesforce"
              isOauth={true}
              oauthLink={SaleseforceAuthUrl}
              title="Connect account"
              connectorType="button"
              buttonType="primary"
              className="salesforce-integration-connector"
              oauthQueryParams={SalesforcePayload}
              logLoginFail={true} />

            <IntegrationConnector
              onIntegrationConnected={this.onIntegrationConnected}
              integrationId="salesforceSandbox"
              isOauth={true}
              oauthLink={SandboxURL}
              title="Connect sandbox account"
              connectorType="button"
              className="salesforce-integration-connector"
              oauthQueryParams={SandboxPayload}
              logLoginFail={true} />
          </div>
        }

        {(isConnected && hasAccess) && this.renderSalesforceTogglesLink()}
        {(isConnected && hasAccess && hasSalesforceAttributesMapping) && this.renderSalesforceMappingLink()}
        {(isConnected && hasAccess && hasSalesforceUserMapping) && this.renderSalesforceUserMappingLink()}
        {(isConnected && hasAccess) && this.renderSalesforceChatMetricsLink()}

        {isConnected && hasAccess && hasSalesforceRevenueDashboard && <RevenueDashboardToggle/>}

        {isConnected && (
          <Link to="/settings/salesforce/event-log" className="integration-config-link">
            View Salesforce event log
          </Link>
        )}

      </div>
    )
  }
}

const mapStateToProps = state => ({
  settingsAppConfig: settingsAppConfig(state)
})

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    refreshCurrentUser,
    patchOrgProperties
  }, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withNotifications(Salesforce)
)



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/services/Salesforce.js